/**
 * Verification feature service layer.
 *
 * There is not yet a production Verification controller. Context data is
 * composed from existing authoritative APIs instead: AppTask owns the linked
 * tag ids, Tags own support metadata/thumbnail, and Documents owns linked ISO
 * drawings. Components remain independent of transport details and demo data.
 */
import { GET_ALL_DOCUMENTS } from '@/utils/attachmentsAPI';
import HttpService from '@/utils/httpService';
import { log as debugLog } from '@/utils/debugLogger';

const DOCUMENT_PAGE_SIZE = 100;

/**
 * The Documents endpoint is paged. Keep fetching until `total` is satisfied so
 * the reference selector never silently truncates a tag/task document set.
 */
async function getAllDocumentPages(filters) {
  const documents = [];
  let page = 1;
  let total = 0;

  do {
    const result = await GET_ALL_DOCUMENTS({
      ...filters,
      Page: page,
      ItemsPerPage: DOCUMENT_PAGE_SIZE,
      SortBy: 'DocumentNumber',
      SortDesc: false,
    });
    const items = Array.isArray(result?.items) ? result.items : [];
    total = Number(result?.total) || 0;
    documents.push(...items);
    page += 1;

    if (!items.length) break;
  } while (documents.length < total);

  return { items: documents, total };
}

export default {
  /**
   * Fetch the authoritative AppTask for the verification route.
   *
   * @param {string} taskId
   * @returns {Promise<object>} HttpService response containing TaskVM.
   */
  getByTaskId(taskId) {
    debugLog('[VerificationService] getByTaskId', taskId);
    return HttpService.get({ url: `api/Tasks/${taskId}` });
  },

  /** Backward-compatible alias for older call sites. */
  getTaskById(taskId) {
    return this.getByTaskId(taskId);
  },

  /**
   * Resolve documents linked to a verified tag through the existing
   * authorized Documents TypeHead endpoint.
   */
  getDocumentsForTag({ tagId, assetId }) {
    if (!tagId) return Promise.resolve({ items: [], total: 0 });
    debugLog('[VerificationService] getDocumentsForTag', { tagId, assetId });
    const filters = {
      ForgeTagsIds: [tagId],
    };
    if (assetId) filters.AssetId = assetId;
    return getAllDocumentPages(filters);
  },

  /**
   * Resolve documents attached directly to the verification task. This is a
   * separate query so the UI can group task and tag relationships truthfully.
   */
  getDocumentsForTask({ taskId, assetId }) {
    if (!taskId) return Promise.resolve({ items: [], total: 0 });
    debugLog('[VerificationService] getDocumentsForTask', { taskId, assetId });
    const filters = { TaskId: taskId };
    if (assetId) filters.AssetId = assetId;
    return getAllDocumentPages(filters);
  },
};
